// src/screens/s2/BusinessActionPlan.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 15 — Business Action Plan + PDF export.
// Four time-boxed sections (Immediate / 30 days / 90 days / Ongoing) filled
// with auto-detected items from this session's S2 data plus adviser free-text
// items persisted on ClientData.s2ActionPlanItems. A PDFDownloadLink button
// builds the 4-page Motion Accountancy branded PDF via @react-pdf/renderer.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useMemo, useState } from 'react';
import { PDFDownloadLink, Document, Page, View, Text, StyleSheet } from '@react-pdf/renderer';
import { ClientData } from '../../types/ClientData';
import { effectiveCTRate } from '../../taxConfig';
import { fvContributions } from '../../utils/planCalculations';
import { formatCurrency, formatAge } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

type SectionKey = 'immediate' | '30d' | '90d' | 'ongoing';
const SECTION_LABELS: Record<SectionKey, string> = {
  immediate: 'Immediate',
  '30d': '30 days',
  '90d': '90 days',
  ongoing: 'Ongoing',
};
const SECTION_COLOURS: Record<SectionKey, string> = {
  immediate: '#DC2626',
  '30d': '#F59E0B',
  '90d': '#60A5FA',
  ongoing: '#10B981',
};

function autoItems(data: ClientData): Array<{ section: SectionKey; text: string }> {
  const items: Array<{ section: SectionKey; text: string }> = [];
  if ((data.s2PensionMonthly ?? 0) > 0) {
    items.push({ section: 'immediate', text: `Set up employer pension SO of ${formatCurrency(data.s2PensionMonthly!)}/mo with pension provider.` });
  }
  if ((data.s2CtReserveMonthly ?? 0) > 0) {
    items.push({ section: 'immediate', text: `Open dedicated CT reserve account and automate ${formatCurrency(data.s2CtReserveMonthly!)}/mo SO.` });
  }
  if ((data.s2VatReserveMonthly ?? 0) > 0) {
    items.push({ section: 'immediate', text: `Automate VAT reserve SO of ${formatCurrency(data.s2VatReserveMonthly!)}/mo.` });
  }
  if (data.businessDlaBalance && data.businessDlaBalance < 0) {
    items.push({ section: 'immediate', text: `DLA overdrawn by ${formatCurrency(Math.abs(data.businessDlaBalance))} — begin repayment via ${data.s2DlaRepaymentRoute ?? '(choose route)'} route.` });
  }
  if ((data.s2HistoricCtLiabilityClearanceMonths ?? 0) > 0) {
    items.push({ section: '30d', text: `Start historic CT/VAT/PAYE clearance — ${data.s2HistoricCtLiabilityClearanceMonths} month plan.` });
  }
  if (data.s2DebtorCollectionTarget && data.s2DebtorCollectionTarget > 0) {
    items.push({ section: '30d', text: `Recover ${data.s2DebtorCollectionTarget}% of aged debtors by ${data.s2DebtorCollectionDate || '(set date)'}.` });
  }
  if (!data.s2LeadsPerMonth || data.s2LeadsPerMonth === 0) {
    items.push({ section: '30d', text: 'Install lead-tracking: log every prospect source + outcome for 90 days.' });
  }
  if (!data.s2ConversionRate || data.s2ConversionRate === 0) {
    items.push({ section: '30d', text: 'Track conversion rate from tracked leads once data lands.' });
  }
  items.push({ section: '90d', text: 'Quarterly review — reconcile Xero figures vs S2 commitments, adjust SOs if needed.' });
  items.push({ section: '90d', text: 'Re-run CT position screen after Q2 to confirm reserve covers projected bill.' });
  items.push({ section: 'ongoing', text: 'Monthly: confirm every S2 SO has run and been recorded against its purpose.' });
  items.push({ section: 'ongoing', text: 'Retention watch: flag any client whose last invoice was >90 days ago.' });
  return items;
}

const addMonths = (d: Date, m: number) => {
  const r = new Date(d);
  r.setMonth(r.getMonth() + m);
  return r;
};

export default function BusinessActionPlan({ data, onUpdate }: Props) {
  const [draft, setDraft] = useState<Record<SectionKey, string>>({ immediate: '', '30d': '', '90d': '', ongoing: '' });

  const autoByKey = useMemo(() => {
    const grouped: Record<SectionKey, string[]> = { immediate: [], '30d': [], '90d': [], ongoing: [] };
    for (const item of autoItems(data)) grouped[item.section].push(item.text);
    return grouped;
  }, [data]);

  const adviserItems = data.s2ActionPlanItems || [];
  const adviserByKey: Record<SectionKey, typeof adviserItems> = {
    immediate: adviserItems.filter((i) => i.section === 'immediate'),
    '30d': adviserItems.filter((i) => i.section === '30d'),
    '90d': adviserItems.filter((i) => i.section === '90d'),
    ongoing: adviserItems.filter((i) => i.section === 'ongoing'),
  };

  const addAdviserItem = (section: SectionKey) => {
    const text = draft[section].trim();
    if (!text) return;
    const id = `${section}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    onUpdate({ s2ActionPlanItems: [...adviserItems, { id, section, text }] });
    setDraft((d) => ({ ...d, [section]: '' }));
  };

  const removeAdviserItem = (id: string) => {
    onUpdate({ s2ActionPlanItems: adviserItems.filter((i) => i.id !== id) });
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-ma-navy">Business Action Plan</h2>
          <p className="text-gray-500 mt-1 text-sm">
            Every commitment agreed today, time-boxed. Auto items are derived
            from S2 data; adviser items persist on the client record.
          </p>
        </div>
        <PDFDownloadLink
          document={<ActionPlanPdf data={data} />}
          fileName={`${(data.clientName || 'client').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-s2-action-plan.pdf`}
          className="px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors"
          style={{ background: '#1E3A5F', color: '#FFFFFF' }}
        >
          {({ loading }) => <span>{loading ? 'Preparing…' : 'Export PDF'}</span>}
        </PDFDownloadLink>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {(Object.keys(SECTION_LABELS) as SectionKey[]).map((key) => (
          <div key={key} className="bg-white rounded-2xl border-l-4 border border-gray-200 p-5" style={{ borderLeftColor: SECTION_COLOURS[key] }}>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: SECTION_COLOURS[key] }}>{SECTION_LABELS[key]}</h3>

            <ul className="space-y-2 mb-4">
              {autoByKey[key].length === 0 && (
                <li className="text-xs text-gray-400 italic">No auto items.</li>
              )}
              {autoByKey[key].map((text, i) => (
                <li key={i} className="text-sm text-gray-700 pl-4 relative">
                  <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full" style={{ backgroundColor: SECTION_COLOURS[key] }} />
                  {text}
                </li>
              ))}
            </ul>

            {adviserByKey[key].length > 0 && (
              <ul className="space-y-2 mb-4 border-t border-gray-100 pt-3">
                {adviserByKey[key].map((item) => (
                  <li key={item.id} className="text-sm text-gray-700 pl-4 relative flex items-start justify-between gap-2">
                    <span className="flex-1">
                      <span className="absolute left-0 top-1.5 w-2 h-2 rounded-full border-2" style={{ borderColor: SECTION_COLOURS[key] }} />
                      {item.text}
                    </span>
                    <button onClick={() => removeAdviserItem(item.id)} className="text-xs text-gray-400 hover:text-red-600 flex-shrink-0">remove</button>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={draft[key]}
                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && addAdviserItem(key)}
                placeholder="Add adviser note…"
                className="flex-1 text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:border-ma-navy"
              />
              <button
                onClick={() => addAdviserItem(key)}
                disabled={!draft[key].trim()}
                className="px-3 py-2 rounded text-sm font-medium disabled:opacity-40"
                style={{ background: SECTION_COLOURS[key], color: '#FFFFFF' }}
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PDF — 4 pages. Motion Accountancy palette only; single-column layout.
// ─────────────────────────────────────────────────────────────────────────────

const NAVY = '#1E3A5F';
const GOLD = '#C9A84C';

const pdfStyles = StyleSheet.create({
  page: { padding: 40, fontSize: 10, color: '#1B2B4B', fontFamily: 'Helvetica' },
  brandBar: { borderBottomWidth: 2, borderBottomColor: GOLD, paddingBottom: 6, marginBottom: 16 },
  brandLine: { fontSize: 9, color: '#6B7280', letterSpacing: 1 },
  title: { fontSize: 20, fontWeight: 700, color: NAVY, marginTop: 2 },
  subtitle: { fontSize: 9, color: '#6B7280', marginTop: 2 },
  h2: { fontSize: 14, fontWeight: 700, color: NAVY, marginTop: 16, marginBottom: 8 },
  h3: { fontSize: 11, fontWeight: 700, color: NAVY, marginTop: 10, marginBottom: 6 },
  body: { fontSize: 10, lineHeight: 1.5, color: '#1B2B4B' },
  row: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 3, borderBottomWidth: 0.5, borderBottomColor: '#E5E7EB' },
  rowLabel: { fontSize: 10, color: '#6B7280', flex: 1 },
  rowValue: { fontSize: 10, color: NAVY, fontWeight: 700 },
  accentCard: { borderWidth: 1, borderColor: GOLD, padding: 8, marginTop: 8, backgroundColor: '#FFF9ED' },
  accentValue: { fontSize: 14, fontWeight: 700, color: GOLD },
  item: { fontSize: 10, marginBottom: 4, paddingLeft: 10, color: '#1B2B4B' },
  footer: { position: 'absolute', bottom: 20, left: 40, right: 40, fontSize: 8, color: '#9CA3AF', borderTopWidth: 0.5, borderTopColor: '#E5E7EB', paddingTop: 4, flexDirection: 'row', justifyContent: 'space-between' },
  threeCol: { flexDirection: 'row', marginTop: 8 },
  threeColCell: { flex: 1, padding: 6 },
  threeColHeader: { fontSize: 9, color: '#6B7280', letterSpacing: 0.5 },
  threeColValue: { fontSize: 13, fontWeight: 700, color: NAVY, marginTop: 2 },
});

function ActionPlanPdf({ data }: { data: ClientData }) {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);

  const beforeS1 = data.s1FreedomAgeBefore ?? data.targetFreedomAge ?? 0;
  const afterS1 = data.targetFreedomAge ?? 0;
  const afterS2 = data.s2FreedomAgeAfter ?? afterS1;
  const gapBeforeS1 = data.s2GapBefore ?? data.gapAmount ?? 0;
  const gapAfterS1 = data.gapAmount ?? 0;
  const gapAfterS2 = data.s2GapAfter ?? gapAfterS1;

  const pensionMonthly = data.s2PensionMonthly ?? 0;
  const ctReserveMonthly = data.s2CtReserveMonthly ?? 0;
  const vatReserveMonthly = data.s2VatReserveMonthly ?? 0;
  const dividendMonthly = data.s2DividendMonthly ?? 0;
  const salaryMonthly = (data.salary || 0) / 12;

  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);
  const growthRate = data.assumptionPensionGrowth || 0.08;
  const ctRate = effectiveCTRate(data.estimatedCompanyProfit || 0);
  const snapshot: Array<{ year: number; revenue: number; ebitda: number; pensionPot: number; ctSaved: number }> = [];
  let rev = data.businessMonthlyRevenue ? data.businessMonthlyRevenue * 12 : (data.estimatedCompanyProfit || 0) * 2;
  const revenueGrowth = data.s2RevenueGrowthRate ?? 0.05;
  const marginPct = 0.55;
  let pot = data.pensionBalance || 0;
  for (let i = 1; i <= Math.min(5, yearsToFreedom); i++) {
    rev = rev * (1 + revenueGrowth);
    const ebitda = rev * marginPct;
    pot = pot * (1 + growthRate) + pensionMonthly * 12;
    const ctSavedThisYear = pensionMonthly * 12 * ctRate;
    snapshot.push({ year: today.getFullYear() + i, revenue: Math.round(rev), ebitda: Math.round(ebitda), pensionPot: Math.round(pot), ctSaved: Math.round(ctSavedThisYear) });
  }

  const adviserItems = data.s2ActionPlanItems || [];
  const autoByKey: Record<SectionKey, string[]> = { immediate: [], '30d': [], '90d': [], ongoing: [] };
  for (const a of autoItems(data)) autoByKey[a.section].push(a.text);

  const clearanceMonths = data.s2HistoricCtLiabilityClearanceMonths ?? 12;
  const clearanceDate = addMonths(today, clearanceMonths).toISOString().slice(0, 10);

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.brandBar}>
          <Text style={pdfStyles.brandLine}>MOTION ACCOUNTANCY — FINANCIAL FREEDOM METHOD</Text>
          <Text style={pdfStyles.title}>Your Freedom Plan, updated</Text>
          <Text style={pdfStyles.subtitle}>{data.clientName || 'Client'} · {todayStr}</Text>
        </View>

        <Text style={pdfStyles.h2}>The arc</Text>
        <View style={pdfStyles.threeCol}>
          {[
            { header: 'BEFORE S1', age: beforeS1, gap: gapBeforeS1 },
            { header: 'AFTER S1',  age: afterS1,  gap: gapAfterS1 },
            { header: 'AFTER S2',  age: afterS2,  gap: gapAfterS2 },
          ].map((col) => (
            <View key={col.header} style={pdfStyles.threeColCell}>
              <Text style={pdfStyles.threeColHeader}>{col.header}</Text>
              <Text style={pdfStyles.threeColValue}>Age {col.age > 0 ? formatAge(col.age) : '—'}</Text>
              <Text style={pdfStyles.subtitle}>Gap {formatCurrency(col.gap)}</Text>
            </View>
          ))}
        </View>

        <View style={pdfStyles.accentCard}>
          <Text style={pdfStyles.subtitle}>TOTAL SESSION IMPACT</Text>
          <Text style={pdfStyles.accentValue}>
            {Math.max(0, afterS1 - afterS2)} year{Math.max(0, afterS1 - afterS2) === 1 ? '' : 's'} gained in this session.
          </Text>
          <Text style={pdfStyles.body}>
            Pension commitment plus CT relief now running. Profit driver lever to be actioned over the next 90 days.
          </Text>
        </View>

        <Text style={pdfStyles.h3}>What this session delivered</Text>
        <Text style={pdfStyles.body}>
          Employer pension contribution of {formatCurrency(pensionMonthly)}/mo agreed —
          delivering {formatCurrency(pensionMonthly * 12 * ctRate)}/yr of CT relief
          and funding {formatCurrency(fvContributions(pensionMonthly, growthRate * 100, yearsToFreedom))} of pension pot
          by age {freedomAge}. Historic liabilities set to clear by {clearanceDate}.
        </Text>

        <View style={pdfStyles.footer} fixed>
          <Text>Motion Accountancy · {data.adviserName || 'Adviser'}</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>

      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.brandBar}>
          <Text style={pdfStyles.brandLine}>SESSION 2 · PAGE 2</Text>
          <Text style={pdfStyles.title}>Business commitments</Text>
        </View>

        <Text style={pdfStyles.h3}>From business account</Text>
        <View style={pdfStyles.row}><Text style={pdfStyles.rowLabel}>Employer pension</Text><Text style={pdfStyles.rowValue}>{formatCurrency(pensionMonthly)}/mo</Text></View>
        <View style={pdfStyles.row}><Text style={pdfStyles.rowLabel}>CT reserve</Text><Text style={pdfStyles.rowValue}>{formatCurrency(ctReserveMonthly)}/mo</Text></View>
        {vatReserveMonthly > 0 && (
          <View style={pdfStyles.row}><Text style={pdfStyles.rowLabel}>VAT reserve</Text><Text style={pdfStyles.rowValue}>{formatCurrency(vatReserveMonthly)}/mo</Text></View>
        )}

        <Text style={pdfStyles.h3}>Director drawings</Text>
        <View style={pdfStyles.row}><Text style={pdfStyles.rowLabel}>Salary (S1)</Text><Text style={pdfStyles.rowValue}>{formatCurrency(salaryMonthly)}/mo</Text></View>
        <View style={pdfStyles.row}><Text style={pdfStyles.rowLabel}>Dividends (S2)</Text><Text style={pdfStyles.rowValue}>{formatCurrency(dividendMonthly)}/mo</Text></View>

        {data.businessDlaBalance && data.businessDlaBalance !== 0 && (
          <>
            <Text style={pdfStyles.h3}>Director's Loan Account</Text>
            <View style={pdfStyles.row}>
              <Text style={pdfStyles.rowLabel}>
                {data.businessDlaBalance < 0 ? 'Overdrawn' : 'Credit'}
                {' — '}
                {data.s2DlaRepaymentRoute ?? 'route not chosen'}
              </Text>
              <Text style={pdfStyles.rowValue}>{formatCurrency(Math.abs(data.businessDlaBalance))}</Text>
            </View>
          </>
        )}

        <Text style={pdfStyles.h3}>Target dates</Text>
        <View style={pdfStyles.row}><Text style={pdfStyles.rowLabel}>Historic liabilities cleared</Text><Text style={pdfStyles.rowValue}>{clearanceDate}</Text></View>
        <View style={pdfStyles.row}><Text style={pdfStyles.rowLabel}>Business EF target (months cover)</Text><Text style={pdfStyles.rowValue}>{data.s2BusinessEfTarget ?? 3} mo</Text></View>
        <View style={pdfStyles.row}><Text style={pdfStyles.rowLabel}>Debtor collection target</Text><Text style={pdfStyles.rowValue}>{data.s2DebtorCollectionTarget ?? 0}% by {data.s2DebtorCollectionDate || '—'}</Text></View>

        <View style={pdfStyles.footer} fixed>
          <Text>Motion Accountancy · {data.adviserName || 'Adviser'}</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>

      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.brandBar}>
          <Text style={pdfStyles.brandLine}>SESSION 2 · PAGE 3</Text>
          <Text style={pdfStyles.title}>Action plan</Text>
        </View>

        {(Object.keys(SECTION_LABELS) as SectionKey[]).map((key) => {
          const autos = autoByKey[key];
          const adv = adviserItems.filter((i) => i.section === key);
          if (autos.length === 0 && adv.length === 0) return null;
          return (
            <View key={key}>
              <Text style={pdfStyles.h3}>{SECTION_LABELS[key].toUpperCase()}</Text>
              {autos.map((t, i) => <Text key={`a-${i}`} style={pdfStyles.item}>• {t}</Text>)}
              {adv.map((item) => <Text key={item.id} style={pdfStyles.item}>• {item.text}</Text>)}
            </View>
          );
        })}

        <View style={pdfStyles.footer} fixed>
          <Text>Motion Accountancy · {data.adviserName || 'Adviser'}</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>

      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.brandBar}>
          <Text style={pdfStyles.brandLine}>SESSION 2 · PAGE 4</Text>
          <Text style={pdfStyles.title}>5-year business snapshot</Text>
          <Text style={pdfStyles.subtitle}>Directional projection — refined each session against live Xero.</Text>
        </View>

        <View style={[pdfStyles.row, { backgroundColor: '#F3F4F6' }]}>
          <Text style={pdfStyles.rowLabel}>Year</Text>
          <Text style={pdfStyles.rowLabel}>Revenue</Text>
          <Text style={pdfStyles.rowLabel}>EBITDA</Text>
          <Text style={pdfStyles.rowLabel}>Pension pot</Text>
          <Text style={pdfStyles.rowLabel}>CT saved</Text>
        </View>
        {snapshot.map((row) => (
          <View key={row.year} style={pdfStyles.row}>
            <Text style={pdfStyles.rowLabel}>{row.year}</Text>
            <Text style={pdfStyles.rowValue}>{formatCurrency(row.revenue)}</Text>
            <Text style={pdfStyles.rowValue}>{formatCurrency(row.ebitda)}</Text>
            <Text style={pdfStyles.rowValue}>{formatCurrency(row.pensionPot)}</Text>
            <Text style={pdfStyles.rowValue}>{formatCurrency(row.ctSaved)}</Text>
          </View>
        ))}

        <View style={pdfStyles.accentCard}>
          <Text style={pdfStyles.subtitle}>ABOUT THIS PROJECTION</Text>
          <Text style={pdfStyles.body}>
            Revenue growth at {Math.round(revenueGrowth * 100)}% per year. Effective
            CT rate {Math.round(ctRate * 1000) / 10}%. Full year-by-year projection
            and assumption panel are in the live P&amp;L Projection screen.
          </Text>
        </View>

        <View style={pdfStyles.footer} fixed>
          <Text>Motion Accountancy · {data.adviserName || 'Adviser'}</Text>
          <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
        </View>
      </Page>
    </Document>
  );
}
